import { useState, useEffect, useCallback } from "react";
import { Modal } from "flowbite-react";
import {
  useNettingAccounts,
  useRebalancePreview,
  usePerformRebalanceMutation,
} from "@repo/api";
import { useToast, useTranslation } from "@repo/core";
import { ModalNavigation } from "./components/ModalNavigation";
import { OverviewPage } from "./pages/OverviewPage";
import { PreviewPage } from "./pages/PreviewPage";
import { ResultsPage } from "./pages/ResultsPage";
import type { RoboMarketsModalProps, ModalState, ModalPage } from "./types";

const getModalTitle = (page: ModalPage, tStrategy: (key: string) => string): string => {
  switch (page) {
    case "overview":
      return tStrategy("tradeOnRoboMarkets");
    case "preview":
      return tStrategy("rebalancePreview");
    case "results":
      return tStrategy("rebalanceResults");
    default:
      return tStrategy("tradeOnRoboMarkets");
  }
};

/**
 * RoboMarkets trading modal component
 */
export const RoboMarketsModal = ({
  isOpen,
  onClose,
  strategy,
}: RoboMarketsModalProps) => {
  const [modalState, setModalState] = useState<ModalState>({
    selectedAccountId: "",
    isDropdownOpen: false,
    currentPage: "overview",
    rebalanceResults: null,
    isConfirmed: false,
  });

  const { addSuccessMessage, addErrorMessage } = useToast();
  const { tStrategy } = useTranslation();

  // API hooks
  const nettingAccountsQuery = useNettingAccounts(undefined, {
    enabled: isOpen,
  });

  const rebalancePreviewQuery = useRebalancePreview(
    modalState.selectedAccountId || "",
    strategy?.modelId || "",
    undefined,
    {
      enabled:
        isOpen &&
        modalState.currentPage === "preview" &&
        !!modalState.selectedAccountId &&
        !!strategy?.modelId,
    }
  );

  const { mutate: performRebalance, isPending: isExecuting } =
    usePerformRebalanceMutation();

  // Initialize selected account when accounts are loaded
  useEffect(() => {
    if (
      isOpen &&
      nettingAccountsQuery.data &&
      nettingAccountsQuery.data.length > 0 &&
      !modalState.selectedAccountId
    ) {
      setModalState((prev) => ({
        ...prev,
        selectedAccountId: nettingAccountsQuery.data![0].id,
      }));
    }
  }, [isOpen, nettingAccountsQuery.data, modalState.selectedAccountId]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setModalState({
        selectedAccountId: "",
        isDropdownOpen: false,
        currentPage: "overview",
        rebalanceResults: null,
        isConfirmed: false,
      });
    }
  }, [isOpen]);

  // Handler functions
  const handleAccountSelect = (accountId: string) => {
    setModalState((prev) => ({
      ...prev,
      selectedAccountId: accountId,
    }));
  };

  const handlePageChange = (page: ModalPage) => {
    setModalState((prev) => ({
      ...prev,
      currentPage: page,
      isDropdownOpen: false,
    }));
  };

  const handleSetDropdownOpen = (open: boolean) => {
    setModalState((prev) => ({
      ...prev,
      isDropdownOpen: open,
    }));
  };

  const handleConfirmationChange = useCallback((confirmed: boolean) => {
    setModalState((prev) => ({
      ...prev,
      isConfirmed: confirmed,
    }));
  }, []);

  const handleExecuteRebalance = () => {
    if (!modalState.selectedAccountId || !strategy?.modelId) {
      addErrorMessage(tStrategy("missingRebalanceInfo"));
      return;
    }

    performRebalance(
      {
        accountId: modalState.selectedAccountId,
        modelId: strategy.modelId,
      },
      {
        onSuccess: (data) => {
          addSuccessMessage(tStrategy("rebalanceSuccess"));
          setModalState((prev) => ({
            ...prev,
            currentPage: "results",
            rebalanceResults: data,
          }));
        },
        onError: (error) => {
          console.error("Error executing rebalance:", error);
          addErrorMessage(tStrategy("rebalanceError"));
        },
      }
    );
  };

  // A successful request that returns an empty list is the netting/hedging
  // case: the user has RoboMarkets accounts, just none we can trade on. Keyed
  // off isSuccess so the empty state isn't shown while the request is still in
  // flight or after it failed - failures are reported by their own alert.
  const hasNoNettingAccounts = Boolean(
    nettingAccountsQuery.isSuccess && nettingAccountsQuery.data?.length === 0
  );

  // Determine navigation capabilities
  const canNext = Boolean(
    modalState.currentPage === "overview" &&
    modalState.selectedAccountId &&
    strategy?.modelId
  );

  // Check if there are any actual orders (sum of order.order !== 0)
  const hasValidOrders = Boolean(
    rebalancePreviewQuery.data?.orders &&
    Object.values(rebalancePreviewQuery.data.orders).some(
      (order) => order.order !== 0
    )
  );

  const canExecute = Boolean(
    modalState.currentPage === "preview" &&
      rebalancePreviewQuery.data &&
      modalState.isConfirmed &&
      hasValidOrders
  );

  return (
    <Modal show={isOpen} onClose={onClose} size="7xl" className="robomarkets-modal">
      <div className="flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-xl font-semibold text-gray-900">
            {getModalTitle(modalState.currentPage, tStrategy)}
          </h3>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 px-6 py-6 overflow-y-auto min-h-0">
          {modalState.currentPage === "overview" && (
            <OverviewPage
              strategy={strategy}
              selectedAccountId={modalState.selectedAccountId}
              onAccountSelect={handleAccountSelect}
              isDropdownOpen={modalState.isDropdownOpen}
              setIsDropdownOpen={handleSetDropdownOpen}
              nettingAccountsError={nettingAccountsQuery.error}
              hasNoNettingAccounts={hasNoNettingAccounts}
              onCloseParentModal={onClose}
            />
          )}

          {modalState.currentPage === "preview" && (
            <PreviewPage
              strategy={strategy}
              selectedAccountId={modalState.selectedAccountId}
              onConfirmationChange={handleConfirmationChange}
            />
          )}

          {modalState.currentPage === "results" && (
            <ResultsPage rebalanceResults={modalState.rebalanceResults} />
          )}
        </div>

        {/* Modal Footer - Fixed */}
        <div className="border-t border-gray-200 px-6 pb-6 pt-4 flex-shrink-0">
          <ModalNavigation
            currentPage={modalState.currentPage}
            onNext={() => handlePageChange("preview")}
            onBack={() => handlePageChange("overview")}
            onClose={onClose}
            onExecute={handleExecuteRebalance}
            canNext={canNext}
            canExecute={canExecute}
            isExecuting={isExecuting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RoboMarketsModal;
